<script lang="ts">
	import { format } from 'svelte-i18n';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { ComponentSize } from '@Shelly/shelly-design-system/helpers/enums';
	import { Icon, Spinner, type IconPropsTypes } from '@Shelly/shelly-design-system/index';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import { createEventDispatcher } from 'svelte';

	export { className as class };
	let className = '';
	export let src: string = '';
	export let size: IconPropsTypes['size'] | 'full' | 'preview-s' = ComponentSize.M;
	export let overlayIcon: IconPropsTypes['icon'] = null;
	/** Shows a spinner in place of `overlayIcon` while the image is being resolved. */
	export let loading = false;
	export let aspectRatio: '16-9' | '4-3' = null;
	export let borderRadius: 'small' | 'medium' | 'big' | 'extra-big' = 'big';

	const placeholder = load_image_from('images/device_images/logo_white_S.png');

	const handleError = () => {
		src = placeholder;
	};
	const dispatch = createEventDispatcher();

	/** Fall back to the logo placeholder for empty/missing src, not only on load error. */
	$: imageSrc = src || placeholder;

	/** Only the full-size preview has room for the larger spinner. */
	$: spinnerSize = size === 'full' ? 'm' : 's';

	$: currentClasses = classMap({
		[className]: true,
		['preview-holder']: true,
		[`width-${size}`]: true,
		[`aspect-${aspectRatio}`]: !!aspectRatio,
		[`border-radius-${borderRadius}`]: !!borderRadius,
	});
</script>

<div role="presentation" {...$$restProps} class={currentClasses} on:click={(e) => dispatch('click', e)}>
	<img
		on:error={handleError}
		src={imageSrc}
		alt={$format('camera_recording_thumbnail_alt', { default: 'Recording thumbnail' })}
		class="preview-image"
	/>
	{#if loading || overlayIcon}
		<div
			class="preview-overlay"
			aria-busy={loading}
			aria-label={loading
				? $format('camera_recording_thumbnail_loading', { default: 'Loading thumbnail' })
				: undefined}
		>
			{#if loading}
				<Spinner size={spinnerSize} />
			{:else}
				<Icon icon={overlayIcon} />
			{/if}
		</div>
	{/if}
	{#if $$slots.topLeft}
		<div class="slot-top-left pt-s pl-s max-width-full">
			<slot name="topLeft" />
		</div>
	{/if}
</div>

<style>
	.preview-holder {
		background: var(--color-base);
		height: 100%;
		overflow: hidden;
		position: relative;
	}
	.preview-image {
		object-fit: cover;
		width: 100%;
		height: 100%;
	}
	.preview-overlay {
		z-index: 1;
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		background: rgba(0, 0, 0, 0.4);
		display: flex;
		align-items: center;
		justify-content: center;
		transition: opacity 0.3s;
	}

	/* Top-left overlay: matches dashboard / widget badge placement (top: 0, left: 0, z-index above center overlay). */
	.slot-top-left {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 2;
		pointer-events: none;
		box-sizing: border-box;
	}

	.width-preview-s {
		width: 80px;
	}

	.aspect-16-9 {
		aspect-ratio: 16/9;
	}
	.aspect-4-3 {
		aspect-ratio: 4/3;
	}

	.border-radius-small {
		border-radius: var(--radius-small);
	}
	.border-radius-medium {
		border-radius: var(--radius-medium);
	}
	.border-radius-big {
		border-radius: var(--radius-big);
	}
	.border-radius-extra-big {
		border-radius: var(--radius-extra-big);
	}
</style>
